import { useState } from 'react';
import { useSetFwReady, useSetFwReadyConfirmed } from '../lib/fwReady';
import { cn, formatDateCompact, toInputDate } from '../lib/utils';

interface FwReadyCellProps {
    jobId: string;
    value: string | null; // YYYY-MM-DD or null
    readyConfirmed: boolean;
    canEdit: boolean;
    /**
     * Compact row. The Mark ready chip is the second of two stacked elements
     * here, so it comes off. The orange cell tint still says the builder gave
     * the go-ahead, which is the part that has to survive a screen grab. To
     * toggle it she switches back to the full rows.
     */
    compact?: boolean;
}

/**
 * FW Ready cell. Two facts live here, not one.
 *
 * The date is when flatwork is expected to be workable. The orange state is the
 * builder having actually said go. Andrea keeps both on her start sheet today,
 * a date in the cell and the community and lot turned orange once the call
 * comes, so the board carries both or the colour has to live in her head.
 *
 * Click the date to edit it. Click the chip underneath to toggle the go-ahead.
 * The chip works with no date set, because builders call before anyone writes
 * one down.
 */
export function FwReadyCell({ jobId, value, readyConfirmed, canEdit, compact = false }: FwReadyCellProps) {
    const [editing, setEditing] = useState(false);
    const setMut = useSetFwReady();
    const confirmMut = useSetFwReadyConfirmed();

    function commit(next: string) {
        const v = next || null;
        if (v !== (value ?? null)) setMut.mutate({ jobId, fwReady: v });
        setEditing(false);
    }

    // Same orange the board already uses for in-progress work, so the colour
    // language stays consistent rather than introducing a second orange.
    // Stronger in compact, where the "Ready" label is hidden and the tint is the
    // only thing saying the builder called.
    const readyClasses = compact
        ? 'bg-orange-500/[0.45] border-orange-500/40'
        : 'bg-orange-500/15 border-orange-500/40';

    if (!canEdit) {
        return (
            <div
                className={cn(
                    compact ? 'px-3 py-0.5' : 'px-3 py-2.5',
                    'border-r border-graphite-200 dark:border-graphite-900 flex flex-col justify-center gap-1 font-mono text-xs tabular',
                    readyConfirmed ? readyClasses : ''
                )}
            >
                <span
                    className={cn(
                        readyConfirmed
                            ? 'text-orange-700 dark:text-orange-400'
                            : 'text-graphite-700 dark:text-graphite-300'
                    )}
                >
                    {value ? formatDateCompact(value) : <span className="text-graphite-300 dark:text-graphite-700">—</span>}
                </span>
                {readyConfirmed && !compact ? (
                    <span className="font-mono text-[9px] uppercase tracking-wider text-orange-700 dark:text-orange-400">
                        Ready
                    </span>
                ) : null}
            </div>
        );
    }

    if (editing) {
        return (
            <div className="px-2 py-2 border-r border-graphite-200 dark:border-graphite-900 flex items-center">
                <input
                    type="date"
                    autoFocus
                    defaultValue={toInputDate(value)}
                    onBlur={(e) => commit(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') commit((e.target as HTMLInputElement).value);
                        if (e.key === 'Escape') setEditing(false);
                    }}
                    className="w-full bg-transparent border border-signal-500 rounded px-1.5 py-1 font-mono text-xs text-graphite-900 dark:text-graphite-100 focus:outline-none"
                />
            </div>
        );
    }

    return (
        <div
            className={cn(
                'border-r border-graphite-200 dark:border-graphite-900 flex flex-col min-w-0 transition-colors',
                readyConfirmed && readyClasses
            )}
        >
            <button
                type="button"
                onClick={() => setEditing(true)}
                title={value ? 'Click to change the FW Ready date' : 'Click to set a FW Ready date'}
                className={cn(
                    compact ? 'px-3 py-0.5' : 'px-3 pt-2 pb-0.5',
                    'flex items-center font-mono text-xs tabular text-left transition-colors cursor-pointer',
                    'hover:bg-graphite-200/60 dark:hover:bg-graphite-800/60',
                    readyConfirmed
                        ? 'text-orange-700 dark:text-orange-400'
                        : value
                            ? 'text-graphite-700 dark:text-graphite-300'
                            : 'text-graphite-300 dark:text-graphite-700'
                )}
            >
                {value ? formatDateCompact(value) : '—'}
            </button>

            {compact ? null : (
            <button
                type="button"
                disabled={confirmMut.isPending}
                onClick={() => confirmMut.mutate({ jobId, confirmed: !readyConfirmed })}
                title={
                    readyConfirmed
                        ? 'Builder has confirmed flatwork is ready. Click to undo.'
                        : 'Mark that the builder has confirmed flatwork is ready'
                }
                className={cn(
                    'mx-2 mb-2 px-1.5 py-0.5 rounded border font-mono text-[9px] uppercase tracking-wider text-center transition-colors disabled:opacity-50',
                    readyConfirmed
                        ? 'border-orange-500/50 text-orange-700 dark:text-orange-400 hover:bg-orange-500/20'
                        : 'border-graphite-300 dark:border-graphite-700 text-graphite-400 dark:text-graphite-600 hover:border-orange-500/50 hover:text-orange-700 dark:hover:text-orange-400'
                )}
            >
                {confirmMut.isPending ? '...' : readyConfirmed ? 'Ready' : 'Mark ready'}
            </button>
            )}
        </div>
    );
}
